import {
  Note,
  ArticleReference,
  ExternalReference,
  SourceContentArea,
  Mark,
} from '../../redux/store/api/api';
import {
  GENERAL_MARK,
  MARK,
  USER_GROUP_MARK,
  USER_MARK,
} from '../../shared/constants';
import { NoteVisibilityType } from '../../shared/enums';
import {
  getCategoryReferenceUrl,
  getArticleUrl,
} from '../../shared/urlBuilder';
import { VisibilityGroupString } from '../notes/types';
import { contentReferenceClass } from './functions/nodes';
import { ContentArea } from './types';

interface IGetContentAreaFromNoteProps {
  note: Note;
  classNames?: string[];
  clickableSpan?: boolean;
  idPrefix?: string;
}

export const getContentAreaFromNote = ({
  note,
  classNames,
  clickableSpan,
  idPrefix,
}: IGetContentAreaFromNoteProps): ContentArea => {
  const { mark } = note;
  let defaultClassNames: string[] = [];
  const visibility = NoteVisibilityType[
    note?.visibility || 0
  ] as VisibilityGroupString;
  switch (visibility) {
    case NoteVisibilityType[NoteVisibilityType.User]:
      defaultClassNames = [MARK, USER_MARK];
      break;
    case NoteVisibilityType[NoteVisibilityType.UserGroup]:
      defaultClassNames = [MARK, USER_GROUP_MARK];
      break;
    case NoteVisibilityType[NoteVisibilityType.General]:
      defaultClassNames = [MARK, GENERAL_MARK];
      break;
    default:
      break;
  }

  return {
    id: mark?.id || '',
    startNodeContainerId: mark?.startNodeContainerId || null,
    startNodeData: mark?.startNodeData || '',
    endNodeData: mark?.endNodeData || '',
    patternString: mark?.patternString || '',
    precedingCharacters: mark?.precedingCharacters ?? null,
    subsequentCharacters: mark?.subsequentCharacters ?? null,
    parentId: mark?.noteId || '',
    invalid: mark?.markInvalid,
    autoUpdated: mark?.markAutoUpdated,
    versionId: note.versionId || '',
    changed: false,
    tagName: 'mark',
    classNames: classNames?.length ? classNames : defaultClassNames,
    clickableSpan: clickableSpan ?? false,
    idPrefix,
  };
};

interface IGetContentAreaFromArticleReferenceProps {
  reference: ArticleReference;
  iconClassName?: string;
  description?: string;
  classNames?: string[];
  idPrefix?: string;
}

export const getContentAreaFromArticleReference = ({
  reference,
  iconClassName,
  description,
  classNames,
  idPrefix,
}: IGetContentAreaFromArticleReferenceProps): ContentArea => {
  const { sourceContentArea } = reference;
  const articleIdForHref = reference?.targetArticleId;
  const contentAreaId =
    !reference?.targetCategoryId && reference?.targetContentAreaId
      ? reference?.targetContentAreaId
      : undefined;
  const href = reference?.targetCategoryId
    ? getCategoryReferenceUrl(reference?.targetCategoryId)
    : getArticleUrl(articleIdForHref || '', undefined, contentAreaId);
  const defaultClassNames = [contentReferenceClass];

  return {
    id: sourceContentArea?.id || '',
    startNodeContainerId: sourceContentArea?.startNodeContainerId || null,
    startNodeData: sourceContentArea?.startNodeData || '',
    endNodeData: sourceContentArea?.endNodeData || '',
    patternString: sourceContentArea?.patternString || '',
    precedingCharacters: sourceContentArea?.precedingCharacters ?? null,
    subsequentCharacters: sourceContentArea?.subsequentCharacters ?? null,
    parentId: sourceContentArea?.articleReferenceId || '',
    invalid: sourceContentArea?.areaInvalid,
    autoUpdated: sourceContentArea?.areaAutoUpdated,
    changed: false,
    tagName: 'a',
    href,
    iconClassName,
    classNames: classNames?.length ? classNames : defaultClassNames,
    description,
    idPrefix,
  };
};

interface IGetContentAreaFromExternalReferenceProps {
  reference: ExternalReference;
  iconClassName?: string;
  description?: string;
  classNames?: string[];
  idPrefix?: string;
}

export const getContentAreaFromExternalReference = ({
  reference,
  iconClassName,
  description,
  classNames,
  idPrefix,
}: IGetContentAreaFromExternalReferenceProps): ContentArea => {
  const { sourceContentArea } = reference;
  const href = reference?.hyperlink || '';
  const defaultClassNames = [contentReferenceClass];

  return {
    id: sourceContentArea?.id || '',
    startNodeContainerId: sourceContentArea?.startNodeContainerId || null,
    startNodeData: sourceContentArea?.startNodeData || '',
    endNodeData: sourceContentArea?.endNodeData || '',
    patternString: sourceContentArea?.patternString || '',
    precedingCharacters: sourceContentArea?.precedingCharacters ?? null,
    subsequentCharacters: sourceContentArea?.subsequentCharacters ?? null,
    parentId:
      sourceContentArea?.articleReferenceId ||
      sourceContentArea?.externalReferenceId ||
      '',
    invalid: sourceContentArea?.areaInvalid,
    autoUpdated: sourceContentArea?.areaAutoUpdated,
    changed: false,
    tagName: 'a',
    href,
    target: '_blank',
    rel: 'noopener noreferrer',
    iconClassName,
    classNames: classNames?.length ? classNames : defaultClassNames,
    description,
    idPrefix,
  };
};

const getSourceContentAreaFromContentArea = (
  contentArea: ContentArea,
): SourceContentArea => ({
  id: contentArea?.id || '',
  startNodeContainerId: contentArea?.startNodeContainerId || '',
  startNodeData: contentArea?.startNodeData || '',
  endNodeData: contentArea?.endNodeData || '',
  patternString: contentArea?.patternString || '',
  precedingCharacters: contentArea?.precedingCharacters || '',
  subsequentCharacters: contentArea?.subsequentCharacters || '',
  articleReferenceId: contentArea?.parentId || '',
  areaInvalid: contentArea?.invalid,
  areaAutoUpdated: contentArea?.autoUpdated,
});

const getMarkFromContentArea = (contentArea: ContentArea): Mark => ({
  id: contentArea?.id || '',
  startNodeContainerId: contentArea?.startNodeContainerId || '',
  startNodeData: contentArea?.startNodeData || '',
  endNodeData: contentArea?.endNodeData || '',
  patternString: contentArea?.patternString || '',
  precedingCharacters: contentArea?.precedingCharacters || '',
  subsequentCharacters: contentArea?.subsequentCharacters || '',
  noteId: contentArea?.parentId || '',
  markInvalid: contentArea?.invalid,
  markAutoUpdated: contentArea?.autoUpdated,
});

export const getSpecificTypesFromContentAreas = (
  contentAreas: ContentArea[],
): Mark[] | SourceContentArea[] =>
  contentAreas.map((a) =>
    a.tagName === 'a'
      ? getSourceContentAreaFromContentArea(a)
      : getMarkFromContentArea(a),
  );
